import { useCallback } from 'react';
import { clipboardHasText } from '../../../utils/clipboardMenu';
import { openFolderVariablesFor } from '../../../utils/openFolderVariables';
import { selectionIntersectsVariable } from './syntaxHighlightHelpers';

/** Mouse, focus, keyboard, paste and variable-editing handlers. */
export function useHighlightHandlers({
  value,
  onChange,
  selectedEnvironment,
  onVariableUpdate,
  variableContext,
  onPastePlainText,
  onBlurProp,
  disabled,
  showDynamicVarPicker,
  type,
  expandOnOverflow,
  selectionToVariable,
  props,
  setTooltip,
  setIsFocused,
  editingVariable,
  setEditingVariable,
  selectionMenu,
  setSelectionMenu,
  dynSuggestions,
  setDynSuggestions,
  isExpanded,
  setIsExpanded,
  editableRef,
  lastCaretRef,
  editingVariableRef,
  selectionMenuRef,
  suppressExpandRef,
  lastValueRef,
  getVariableValue,
  findVariableOptions,
  renderHighlightedContent,
  getPlainText,
  getSelectionOffsets,
  stripDomNewlines,
  setSelectionOffsets,
  openDynamicVarPicker,
  scheduleDynamicVarPicker,
  undoStackRef,
  redoStackRef,
  lastOpRef,
  commitHistory,
  expandIfOverflowing,
}) {
  // Handle tooltip on hover
  const handleMouseMove = (e) => {
    if (!editableRef.current || editingVariable) return;
    
    const element = e.target;
    if (element && element.dataset && element.dataset.variable) {
      setTooltip({
        name: element.dataset.variable,
        value: element.dataset.value,
        type: element.dataset.type,
        folderName: element.dataset.type === 'folder'
          ? getVariableValue(element.dataset.variable)?.folderName
          : undefined,
        exists: element.dataset.exists === 'true',
        isDynamic: element.dataset.dynamic === 'true',
        x: e.clientX,
        y: e.clientY
      });
    } else {
      setTooltip(null);
    }
  };

  const handleMouseLeave = () => {
    if (!editingVariable) {
      setTooltip(null);
    }
  };

  // Scope a NEW variable lands in when nothing more specific is known: the first available
  // scope in the resolution order (environment beats collection since 16.10.2026), so the new
  // value is the one a send then uses. Shared by click-to-edit and "To Variable" so the two
  // flows cannot drift — the ORDER is the contract.
  const resolveDefaultScope = () =>
    (selectedEnvironment ? 'environment' : null) ||
    (variableContext?.collectionId ? 'collection' : null) ||
    (variableContext?.workspaceId ? 'workspace' : null) ||
    'global';

  // Handle clicking on variables to edit them
  const handleVariableClick = (e, variableName, currentValue, variableType, exists) => {
    e.preventDefault();
    e.stopPropagation();

    // A folder-resolved value is edited where it lives. An inline edit would
    // land in collection scope and stay shadowed by the folder (D2).
    if (variableType === 'folder') {
      setTooltip(null);
      openFolderVariablesFor(getVariableValue(variableName), variableContext);
      return;
    }

    const defaultScope = variableType || resolveDefaultScope();

    const nextEditingVariable = {
      name: variableName,
      value: currentValue || '',
      type: defaultScope,
      exists: exists,
      // So reopening a pick-list shows its alternatives rather than dropping them.
      options: findVariableOptions(variableName),
      x: e.clientX,
      y: e.clientY,
    };
    editingVariableRef.current = nextEditingVariable;
    setEditingVariable(nextEditingVariable);
    setTooltip(null);
  };

  /**
   * "To Variable" — right-click on a plain-text selection.
   *
   * The native menu is suppressed ONLY when the custom one is offered; every
   * bail path below leaves the event alone so the browser's menu still shows
   * for a caret, an empty/whitespace selection, a selection touching a
   * `{{token}}`, a disabled field, or a field that never opted in.
   */
  const handleContextMenu = (e) => {
    if (!selectionToVariable || disabled || !editableRef.current) return;
    const sel = getSelectionOffsets();
    const text = getPlainText(editableRef.current);
    const hasSelection = Boolean(sel) && sel.start !== sel.end;
    const selected = hasSelection ? text.slice(sel.start, sel.end) : '';
    // A selection can become a variable unless it is blank or straddles an
    // existing `{{token}}` — replacing half a token would corrupt both.
    const canExtract =
      hasSelection &&
      selected.trim() !== '' &&
      !selectionIntersectsVariable(text, sel.start, sel.end);

    e.preventDefault();
    selectionMenuRef.current = true;
    // `canPaste` starts true and is corrected once the clipboard answers — the
    // menu must appear on the same tick as the click, and reading the clipboard
    // is async. True first means the worst case is a Paste that does nothing,
    // never a disabled Paste that would have worked.
    setSelectionMenu({
      x: e.clientX, y: e.clientY,
      start: sel?.start ?? 0, end: sel?.end ?? 0,
      text, canExtract, hasSelection, canPaste: true,
    });
    clipboardHasText().then((canPaste) => {
      setSelectionMenu((menu) => (menu ? { ...menu, canPaste } : menu));
    });
  };

  const closeSelectionMenu = useCallback(() => {
    selectionMenuRef.current = false;
    setSelectionMenu(null);
  }, [selectionMenuRef, setSelectionMenu]);

  // Opens the editor as "Add variable": VALUE pre-filled with the selection, name empty and
  // editable (the chip is focused, the user types it), scope defaulted like a click on a missing
  // variable would. `replaceRange` is what saveVariableValue writes `{{name}}` back into.
  const openToVariableEditor = () => {
    const menu = selectionMenu;
    if (!menu) return;
    const { start, end, text } = menu;
    const nextEditingVariable = {
      name: '',
      value: text.slice(start, end),
      type: resolveDefaultScope(),
      exists: false,
      nameEditable: true,
      replaceRange: { start, end, text },
      x: menu.x,
      y: menu.y,
    };
    editingVariableRef.current = nextEditingVariable;
    setEditingVariable(nextEditingVariable);
    setTooltip(null);
    closeSelectionMenu();
  };

  // Save variable value. `name` (third arg) is what the popup's chip holds; it only differs
  // from `editingVariable.name` in the "To Variable" flow, where the name started out empty.
  const saveVariableValue = async (newValue, scope, name, options) => {
    // Snapshot which variable this save is for. `onVariableUpdate` is awaited, so by the time it
    // resolves the user may have clicked straight onto a different variable — whose click already
    // opened its own editor (a new object in editingVariableRef). We must not clear that below.
    const savedVariable = editingVariable;
    if (!savedVariable) return;
    const key = name || savedVariable.name;
    // `options` rides along only when the variable has any — see
    // utils/variableOptions.js. An ordinary variable keeps its exact old shape.
    const newVariable = { key, value: newValue, enabled: true, ...(options?.length ? { options } : {}) };

    // "To Variable": replace the selected range with `{{key}}` in the text snapshotted at
    // right-click time. Done SYNCHRONOUSLY, before the awaited persistence below, so the
    // second-click race this function guards against cannot interleave with the rewrite.
    // Same shape as insertDynVar (range replace → event-shaped onChange → repaint) plus an undo
    // entry like handleInput records, so Ctrl+Z returns the original URL.
    if (savedVariable.replaceRange) {
      const { start, end, text } = savedVariable.replaceRange;
      const token = `{{${key}}}`;
      let next = text.slice(0, start) + token + text.slice(end);
      let caret = start + token.length;
      // URL field: newlines are never part of the value (same policy as handleInput).
      if (expandOnOverflow && /[\r\n]/.test(next)) {
        caret = text.slice(0, start).replace(/[\r\n]+/g, '').length + token.length;
        next = next.replace(/[\r\n]+/g, '');
      }
      commitHistory(lastValueRef.current ?? '', lastCaretRef.current, { coalesce: false });
      lastOpRef.current = 'insert';
      redoStackRef.current = [];
      lastValueRef.current = next;
      lastCaretRef.current = { start: caret, end: caret };
      // Event-SHAPED: consumers are plain <input> handlers reading e.target.value.
      onChange?.({ target: { value: next } });
      renderHighlightedContent(next, null, true);
      // Focus first (handleFocus re-baselines lastValueRef from the DOM, which already holds
      // `next`), THEN place the caret right after the token — outside the variable span.
      editableRef.current?.focus();
      setSelectionOffsets({ start: caret, end: caret });
      // The filler arrives with (or just after) the focus that expands the field, so clean twice:
      // now, and on the next frame once the overlay has taken over.
      stripDomNewlines();
      requestAnimationFrame(() => { if (stripDomNewlines()) setSelectionOffsets({ start: caret, end: caret }); });
    }

    if (onVariableUpdate) {
      await onVariableUpdate(newVariable, {
        scope: scope ?? savedVariable.type,
        collectionId: variableContext?.collectionId,
        workspaceId: variableContext?.workspaceId,
        // When an existing variable's scope was changed in the editor, tell the handler where it
        // used to live so it can remove the old copy — otherwise the variable lingers in both
        // scopes and the higher-priority one keeps resolving (e.g. still shows as "collection").
        previousScope: savedVariable.exists ? savedVariable.type : undefined,
        previousCollectionId: variableContext?.collectionId,
      });
    } else {
      window.dispatchEvent(new CustomEvent('updateEnvironmentVariable', {
        detail: { variable: newVariable, environment: selectedEnvironment },
      }));
    }
    // Only close if the editor is still showing the variable we just saved. If a click on another
    // variable already swapped in a new editor, clearing here would close it and force a second
    // click (the bug: clicking B while A's editor is open closed it instead of opening B).
    if (editingVariableRef.current === savedVariable) {
      editingVariableRef.current = null;
      setEditingVariable(null);
    }
  };

  // Handle focus events with better state management
  const handleFocus = () => {
    setIsFocused(true);
    if (editableRef.current) {
      lastValueRef.current = getPlainText(editableRef.current);
    }
    if (showDynamicVarPicker) {
      // Defer until after focus + selection settle (WebView/contenteditable).
      scheduleDynamicVarPicker();
    }
    if (expandOnOverflow) {
      // Defer until after the click's native caret placement settles (same WebView timing
      // issue as the dynamic-var picker above) — checking synchronously here could snapshot
      // the caret before the browser has placed it at the clicked position.
      setTimeout(() => expandIfOverflowing(), 0);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setTooltip(null);
    setDynSuggestions(null);

    // Ensure final state is synchronized when losing focus
    if (editableRef.current) {
      let finalText = getPlainText(editableRef.current);
      if (expandOnOverflow && /[\r\n]/.test(finalText)) {
        finalText = finalText.replace(/[\r\n]+/g, '');
      }
      if (finalText !== value) {
        onChange?.({ target: { value: finalText } });
      }
      lastValueRef.current = finalText;
      renderHighlightedContent(finalText);
    }

    // Don't collapse when this blur is caused by opening the variable-edit popup (it moves focus
    // to its own input) — that's a continuation of editing this field, not the user clicking away.
    // Uses the ref (not the `editingVariable` state) since this can run in the same tick as the
    // click that set it, before React has re-rendered with the new state value.
    // Same for the "To Variable" menu: its item is a <button>, so pressing it blurs the field.
    if (expandOnOverflow && !editingVariableRef.current && !selectionMenuRef.current) {
      setIsExpanded(false);
    }

    onBlurProp?.();
  };

  // Handle click events to fix cursor positioning
  const handleClick = (e) => {
    if (!editableRef.current) return;
    
    // Check if we clicked on a variable span
    const variableSpan = e.target.closest('.variable-span');
    if (variableSpan) {
      // Note: expand suppression for a variable click is set earlier, in handleMouseDown —
      // mousedown reliably precedes the focus-scheduled overflow check, whereas this click handler
      // can run in a later task than that check in some WebViews (which would let the field expand
      // for a frame before the editor opens).
      if (variableSpan.dataset.dynamic === 'true') {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      
      const variableName = variableSpan.dataset.variable;
      // The chip only carries the DISPLAY value (masked for a secret); the
      // editor needs the real one or saving would write the mask back.
      const currentValue = getVariableValue(variableName)?.value ?? variableSpan.dataset.value;
      const variableType = variableSpan.dataset.type;
      const exists = variableSpan.dataset.exists === 'true';
      
      handleVariableClick(e, variableName, currentValue, variableType, exists);
      return;
    }
    
    // Check if we clicked at the very end of the input
    const rect = editableRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    
    // If click is near the right edge, position cursor at the very end
    if (clickX > rect.width - 20) {
      setTimeout(() => {
        const selection = window.getSelection();
        const range = document.createRange();
        
        // Position cursor at the very end of all content
        range.selectNodeContents(editableRef.current);
        range.collapse(false); // collapse to end
        
        selection.removeAllRanges();
        selection.addRange(range);
        if (showDynamicVarPicker) {
          openDynamicVarPicker({ source: 'pointer' });
        }
      }, 0);
      return;
    }

    if (showDynamicVarPicker) {
      scheduleDynamicVarPicker();
    }
  };

  const refreshDynamicVarPicker = useCallback((e) => {
    if (e && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) return;
    if (showDynamicVarPicker) {
      openDynamicVarPicker();
    }
  }, [showDynamicVarPicker, openDynamicVarPicker]);

  const insertDynVar = (item, suggestion) => {
    if (!editableRef.current || !suggestion) return;
    // Items are objects now (a saved variable and a generated one look
    // different in the list and insert the same way); a bare string is still
    // accepted so nothing that passes a name breaks.
    const varName = typeof item === 'string' ? item : item?.name;
    if (!varName) return;
    const fullText = getPlainText(editableRef.current);
    const { insertStart, insertEnd, mode } = suggestion;
    const end = typeof insertEnd === 'number' ? insertEnd : insertStart;
    // `inner` means the braces are already on screen and only the NAME goes in
    // — but `{{` typed mid-sentence has no closing half yet, and inserting the
    // bare name there left `{{token` sitting unclosed in the middle of the text
    // (owner, 23.09.2026). Close it when nothing already does.
    const closed = /^\s*\}\}/.test(fullText.slice(end));
    const insertion = mode === 'inner'
      ? `${varName}${closed ? '' : '}}'}`
      : `{{${varName}}}`;
    const newText = fullText.substring(0, insertStart) + insertion + fullText.substring(end);
    const newCursor = insertStart + insertion.length;
    lastValueRef.current = newText;
    onChange?.({ target: { value: newText } });
    renderHighlightedContent(newText, { start: newCursor, end: newCursor });
    setDynSuggestions(null);
  };

  // Handle key events with improved behavior
  const applyHistoryEntry = useCallback(
    (entry) => {
      if (!entry) return;
      lastValueRef.current = entry.text;
      // force: the text may be identical to what was last RENDERED while
      // differing from the DOM, and the caret has to be restored either way.
      renderHighlightedContent(entry.text, entry.sel, true);
      if (entry.sel) lastCaretRef.current = entry.sel;
      // Event-SHAPED, like every other onChange here: consumers are wired to
      // plain <input> handlers and read e.target.value. Passing the bare string
      // threw "undefined is not an object (evaluating 'e.target.value')".
      onChange?.({ target: { value: entry.text } });
    },
    [renderHighlightedContent, onChange, lastCaretRef, lastValueRef]
  );

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return false;
    const current = {
      text: getPlainText(editableRef.current),
      sel: getSelectionOffsets() ?? lastCaretRef.current,
    };
    const previous = stack.pop();
    redoStackRef.current.push(current);
    applyHistoryEntry(previous);
    return true;
  }, [getPlainText, getSelectionOffsets, applyHistoryEntry, editableRef, lastCaretRef, redoStackRef, undoStackRef]);

  const redo = useCallback(() => {
    const stack = redoStackRef.current;
    if (!stack.length) return false;
    const current = {
      text: getPlainText(editableRef.current),
      sel: getSelectionOffsets() ?? lastCaretRef.current,
    };
    const next = stack.pop();
    undoStackRef.current.push(current);
    applyHistoryEntry(next);
    return true;
  }, [getPlainText, getSelectionOffsets, applyHistoryEntry, editableRef, lastCaretRef, redoStackRef, undoStackRef]);

  const handleKeyDown = (e) => {
    // Before everything else: undo/redo must work even with the suggestion
    // popup open, which is exactly when a mistyped variable wants taking back.
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        // Ctrl+Shift+Z and Ctrl+Y are both redo; different platforms taught
        // people different ones and neither is worth arguing with.
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (key === 'y') {
        e.preventDefault();
        redo();
        return;
      }
    }

    // Backspace and Delete are applied to the TEXT, not left to the browser.
    //
    // At a variable-span boundary Chromium does not delete a character at all:
    // pressing Backspace with the caret just after `{{host}}` empties the
    // following text node and merges its content into the span. Total text
    // unchanged, structure different — so handleInput read an identical string,
    // concluded nothing had happened, and skipped everything. The keystroke was
    // real and the edit was structural, which is why the key appeared dead
    // exactly where variables are, and worked everywhere else.
    //
    // Since this component already owns rendering and caret restoration,
    // computing the deletion here is both simpler and more predictable than
    // detecting the boundary case and patching it up afterwards. Only COLLAPSED
    // selections are taken over: deleting a real selection already behaves.
    if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      editableRef.current
    ) {
      const sel = getSelectionOffsets();
      if (sel && sel.start === sel.end) {
        const text = getPlainText(editableRef.current);
        const cut = e.key === 'Backspace' ? sel.start - 1 : sel.start;

        // Nothing on that side — let the browser have it, so a Backspace at
        // position 0 still does whatever it would normally do.
        if (cut >= 0 && cut < text.length) {
          e.preventDefault();
          const next = text.slice(0, cut) + text.slice(cut + 1);

          commitHistory(text, sel, { coalesce: lastOpRef.current === 'delete' });
          lastOpRef.current = 'delete';
          redoStackRef.current = [];

          const caret = { start: cut, end: cut };
          lastValueRef.current = next;
          lastCaretRef.current = caret;
          renderHighlightedContent(next, caret, true);
          onChange?.({ target: { value: next } });
          return;
        }
      }
    }

    if (dynSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setDynSuggestions((prev) => prev ? { ...prev, active: Math.min(prev.active + 1, prev.items.length - 1) } : null);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setDynSuggestions((prev) => prev ? { ...prev, active: Math.max(prev.active - 1, 0) } : null);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selected = dynSuggestions.items[dynSuggestions.active];
        if (selected) insertDynVar(selected, dynSuggestions);
        return;
      }
      if (e.key === 'Escape') {
        setDynSuggestions(null);
        return;
      }
    }
    // Prevent Enter in single-line inputs, but give the parent a chance first
    if (e.key === 'Enter' && type !== 'textarea') {
      props.onKeyDown?.(e);
      e.preventDefault();
      return;
    }
    props.onKeyDown?.(e);
  };
  
  // Handle paste events
  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text/plain');
    if (onPastePlainText?.(text)) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    document.execCommand('insertText', false, text);
  };

  const handleMouseDown = (e) => {
    // URL field: a click landing on a variable should open its editor, never expand the collapsed
    // field. Set the suppress flag here on mousedown rather than in onClick, because mousedown
    // always runs before the browser fires focus (and thus before handleFocus schedules its
    // deferred overflow check), whereas onClick can land in a later task than that check in some
    // WebViews — which lets the field expand for a frame. Only needed when the field isn't already
    // focused: an already-focused click fires no focus event, so nothing schedules an expand to
    // suppress, and setting the flag then would leave it stuck and swallow a later real expand.
    if (
      expandOnOverflow &&
      !isExpanded &&
      e.button === 0 &&
      document.activeElement !== editableRef.current &&
      e.target.closest?.('.variable-span')
    ) {
      suppressExpandRef.current = true;
    }
    if (!showDynamicVarPicker || e.button !== 0) return;
    // A press on a highlighted variable is the start of "open its editor", not
    // a request for suggestions — scheduling one here is what made the panel
    // flash before the popup arrived.
    if (e.target.closest?.('.variable-span')) return;
    scheduleDynamicVarPicker();
  };

  return {
    handleMouseMove,
    handleMouseLeave,
    handleContextMenu,
    closeSelectionMenu,
    openToVariableEditor,
    saveVariableValue,
    handleFocus,
    handleBlur,
    handleClick,
    refreshDynamicVarPicker,
    insertDynVar,
    handleKeyDown,
    handlePaste,
    handleMouseDown,
  };
}
